import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Tooltip } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { TAX_TYPE } from "utils/constants";
import { OrderItem } from "utils/types";

type OrderCardType = OrderItem & {
  taxData?: any;
};

const OrderCard = (item: OrderCardType & { timeZone: string }) => {
  const [showProducts, setshowProducts] = useState(false);

  const [date, time] = useMemo(() => {
    /*  if (!item.onDateTime) */ return [item.onDate, item.onTime];
    /*   return convertToTimezone(
      new Date(item.onDateTime),
      TIME_ZONES?.[item.timeZone] || "America/Los_Angeles"
    ).split(", "); */
  }, [item.onDate, item.onTime]);

  const getModifierList = (subItem: any) => {
    const directModifiers = Array.isArray(subItem?.modifiers)
      ? subItem.modifiers
          .map((el: any) => el?.description || el?.modifierName || el?.pizzaModifierName)
          .filter(Boolean)
      : [];

    if (directModifiers.length) {
      return directModifiers.join(", ");
    }

    const dealPizzaModifiers = Array.isArray(subItem?.data?.selectedDealPizzas)
      ? subItem.data.selectedDealPizzas
          .map((dealPizza: any, index: number) => {
            const labels = (dealPizza?.modifiers || [])
              .map((el: any) => el?.description || el?.modifierName || el?.pizzaModifierName)
              .filter(Boolean);

            if (!labels.length) return "";
            const pizzaName =
              dealPizza?.pizzaName || dealPizza?.fullDescription || `Pizza ${index + 1}`;
            return `${pizzaName}: ${labels.join(", ")}`;
          })
          .filter(Boolean)
      : [];

    return dealPizzaModifiers.join(" | ");
  };

  const getItemNote = (subItem: any) => {
    if (String(subItem?.note || "").trim()) return subItem.note;
    if (String(subItem?.notes || "").trim()) return subItem.notes;

    const dealPizzaNotes = Array.isArray(subItem?.data?.selectedDealPizzas)
      ? subItem.data.selectedDealPizzas
          .map((dealPizza: any, index: number) => {
            const note = String(dealPizza?.note || "").trim();
            if (!note) return "";
            const pizzaName =
              dealPizza?.pizzaName || dealPizza?.fullDescription || `Pizza ${index + 1}`;
            return `${pizzaName}: ${note}`;
          })
          .filter(Boolean)
      : [];

    return dealPizzaNotes.join(" | ");
  };

  return (
    <div
      // === ADDED BY SAMPATH – 2026-01-06 ===
      // Modern premium card: light surface, soft shadow, single subtle border, better spacing (keeps theme color via primary/*)
      className="w-full bg-white/70 backdrop-blur-[2px] py-3 px-4 rounded-2xl cursor-pointer select-none border border-primary/15 shadow-sm hover:shadow-md hover:border-primary/25 transition-all"
      // === END SAMPATH ===
      onClick={() => setshowProducts(!showProducts)}
    >
      <div
        // === ADDED BY SAMPATH – 2026-01-06 ===
        // Cleaner header row: softer divider + balanced typography
        className="flex justify-between items-center border-b border-primary/15 px-1 pb-2"
        // === END SAMPATH ===
      >
        <p
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Slightly improved readability on all devices
          className="text-[14px] sm:text-[15px] text-slate-800"
          // === END SAMPATH ===
        >
          <b className="font-semibold text-slate-900">Order ID : </b>
          {item.orderID}
        </p>
        <p
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Total emphasized like a KPI
          className="text-[14px] sm:text-[15px] text-slate-800"
          // === END SAMPATH ===
        >
          <b className="font-semibold text-slate-900">Total : </b>${item.orderTotal.toFixed(2)}
        </p>
      </div>

      <div
        // === ADDED BY SAMPATH – 2026-01-06 ===
        // Better spacing + prevent arrow from stretching layout
        className="flex items-start gap-3 pt-2"
        // === END SAMPATH ===
      >
        <div className="flex-1">
          <div
            // === ADDED BY SAMPATH – 2026-01-06 ===
            // Premium grid: consistent font size, better gaps, and wrapping for long text (Note)
            className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-1.5 py-1 px-1 text-[13px] sm:text-[14px] text-slate-800"
            // === END SAMPATH ===
          >
            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Order Type : </b>
              <span className="text-slate-800">{item.orderType}</span>
            </p>

            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Payment Method : </b>
              <span className="text-slate-800">{item.paymentType || "--"}</span>
            </p>

            <p
              // === ADDED BY SAMPATH – 2026-01-06 ===
              // Prevent “Note” from blowing up the row height; still readable
              className="leading-snug break-words"
              // === END SAMPATH ===
            >
              <b className="font-semibold text-slate-900">Note : </b>
              <span className="text-slate-800">{item.notes || "--"}</span>
            </p>

            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Date : </b>
              <span className="text-slate-800">{date}</span>
            </p>

            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Time : </b>
              <span className="text-slate-800">{time}</span>
            </p>

            <p className="leading-snug">
              {!!item.totalTax1 && (
                <>
                  <b className="font-semibold text-slate-900">
                    {item?.taxData?.[0]?.name || TAX_TYPE.tax1} :{" "}
                  </b>
                  <span className="text-slate-800">
                    ${" "}
                    {(item.totalTax1 || 0).toFixed(2)}
                  </span>
                </>
              )}
            </p>

            <p className="leading-snug">
              {!!item.totalTax2 && (
                <>
                  <b className="font-semibold text-slate-900">
                    {item?.taxData?.[1]?.name || TAX_TYPE.tax2} :{" "}
                  </b>
                  <span className="text-slate-800">
                    ${(item.totalTax2 || 0).toFixed(2)}
                  </span>
                </>
              )}
            </p>

            <p className="leading-snug">
              {!!item.totalTax3 && (
                <>
                  <b className="font-semibold text-slate-900">
                    {item?.taxData?.[2]?.name || TAX_TYPE.tax3} :{" "}
                  </b>
                  <span className="text-slate-800">
                    ${(item.totalTax3 || 0).toFixed(2)}
                  </span>
                </>
              )}
            </p>

            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Tip : </b>
              <span className="text-slate-800">
                $
                {item?.tip?.toFixed(2) ? `${item.tip.toFixed(2)}` : `0.00`}
              </span>
            </p>

            <p className="leading-snug">
              <b className="font-semibold text-slate-900">Sub Total : </b>
              <span className="text-slate-800">${item.subTotal}</span>
            </p>
          </div>

          <AnimatePresence>
            <motion.div
              className="overflow-hidden"
              initial={{ height: 0 }}
              animate={{ height: showProducts ? "auto" : 0 }}
            >
              <div
                // === ADDED BY SAMPATH – 2026-01-06 ===
                // Product block looks like a nested card: single border, soft background, rounded corners
                className="py-2 border border-primary/15 rounded-2xl my-2 bg-primary/5"
                // === END SAMPATH ===
              >
                <h2
                  // === ADDED BY SAMPATH – 2026-01-06 ===
                  // Cleaner section title
                  className="px-3 font-semibold text-slate-900 pb-2 text-[14px] sm:text-[15px]"
                  // === END SAMPATH ===
                >
                  Product Details
                </h2>

                <div
                  // === ADDED BY SAMPATH – 2026-01-06 ===
                  // Header row: subtle background + single divider (no heavy lines)
                  className="flex px-3 border-y border-primary/15 font-semibold text-[12.5px] sm:text-[13.5px] py-2 bg-white/50"
                  // === END SAMPATH ===
                >
                  <p className="flex-[2] sm:flex-1">Product Code</p>
                  <p className="flex-[2] sm:flex-1">Modifiers</p>
                  <p className="flex-1">Quantity</p>
                  <p className="flex-1">Note</p>
                  {!!item.totalTax1 && (
                    <p className="flex-1 max-2md:hidden">
                      {item?.taxData?.[0]?.name || TAX_TYPE.tax1}
                    </p>
                  )}
                  {!!item.totalTax2 && (
                    <p className="flex-1 max-2md:hidden">
                      {item?.taxData?.[1]?.name || TAX_TYPE.tax2}
                    </p>
                  )}
                  {!!item.totalTax3 && (
                    <p className="flex-1 max-2md:hidden">
                      {item?.taxData?.[2]?.name || TAX_TYPE.tax3}
                    </p>
                  )}
                  <p className="flex-1 text-right">Total</p>
                </div>

                <div
                  // === ADDED BY SAMPATH – 2026-01-06 ===
                  // Replace dashed red look with soft modern dividers using theme tint
                  className="flex flex-col divide-y divide-primary/10 mx-3"
                  // === END SAMPATH ===
                >
                  {item.orderDetail.map((subItem, idx) => (
                    <div
                      // === ADDED BY SAMPATH – 2026-01-06 ===
                      // Row spacing + better mobile readability
                      className="flex text-[12.5px] sm:text-[13.5px] py-2 items-start"
                      // === END SAMPATH ===
                      key={`${subItem?.productCode || "item"}-${idx}`} // === ADDED BY SAMPATH – 2026-01-06 === // React key to prevent UI glitches // === END SAMPATH ===
                    >
                      <p className="flex-[2] sm:flex-1 text-slate-800">
                        {subItem.productCode || subItem.description || "--"}
                      </p>

                      <Tooltip
                        title={getModifierList(subItem)}
                        arrow
                        placement="top"
                      >
                        <p
                          // === ADDED BY SAMPATH – 2026-01-06 ===
                          // Keep columns tidy while still showing info with tooltip
                          className="flex-[2] sm:flex-1 truncate text-slate-700"
                          // === END SAMPATH ===
                        >
                          {getModifierList(subItem) || "--"}
                        </p>
                      </Tooltip>

                      <p className="flex-1 text-slate-800">{subItem.quantity}</p>

                      <Tooltip title={getItemNote(subItem)} arrow placement="top">
                        <p
                          // === ADDED BY SAMPATH – 2026-01-06 ===
                          // Prevent long note from breaking row height
                          className="flex-1 truncate text-slate-700"
                          // === END SAMPATH ===
                        >
                          {getItemNote(subItem) || "--"}
                        </p>
                      </Tooltip>

                      {!!item.totalTax1 && (
                        <p className="flex-1 max-2md:hidden text-slate-800">
                          ${subItem.tax1.toFixed(2)}
                        </p>
                      )}
                      {!!item.totalTax2 && (
                        <p className="flex-1 max-2md:hidden text-slate-800">
                          ${subItem.tax2.toFixed(2)}
                        </p>
                      )}
                      {!!item.totalTax3 && (
                        <p className="flex-1 max-2md:hidden text-slate-800">
                          ${subItem.tax3.toFixed(2)}
                        </p>
                      )}

                      <p
                        // === ADDED BY SAMPATH – 2026-01-06 ===
                        // Right-align totals like a report table
                        className="flex-1 text-right font-medium text-slate-900"
                        // === END SAMPATH ===
                      >
                        ${subItem.totalValue}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div
          // === ADDED BY SAMPATH – 2026-01-06 ===
          // Premium expand/collapse icon container (still uses theme color tint)
          className="shrink-0 mt-1 rounded-full border border-primary/15 bg-primary/10 p-1.5 shadow-sm"
          // === END SAMPATH ===
        >
          {showProducts ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </div>
      </div>
    </div>
  );
};

export default OrderCard;
